'use client';

import type { toggleButtonVariants } from '@magicschool/spellbook/components/ToggleButton';
import {
  ToggleGroupItemLeadingIconSlot,
  ToggleGroupItemSlot,
  ToggleGroupItemSpinnerSlot,
  ToggleGroupItemTrailingIconSlot,
  ToggleGroupTooltipContentSlot,
  ToggleGroupTooltipSlot,
} from '@magicschool/spellbook/components/ToggleGroup/slots';
import { useOrderedSlots } from '@magicschool/spellbook/hooks/useOrderedSlots';
import { mapOrderedSlots } from '@magicschool/spellbook/lib/slots';
import { cn } from '@magicschool/spellbook/lib/utils';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cva, type VariantProps } from 'class-variance-authority';

export type ToggleGroupProps = Omit<React.ComponentProps<typeof ToggleGroupPrimitive.Root>, 'value' | 'defaultValue'> &
  VariantProps<typeof toggleButtonVariants> & {
    /** Initial selected value(s) for uncontrolled usage. A `string` for `type="single"`, or `string[]` for `type="multiple"`. */
    defaultValue?: string | string[];
    /** Controlled selected value(s). A `string` for `type="single"`, or `string[]` for `type="multiple"`. */
    value?: string | string[];
    /** If true, the button will display a loading spinner icon and click functionality will be disabled. */
    isLoading?: boolean;
  };

const toggleGroupVariants = cva('group flex max-w-full items-center z-0', {
  variants: {
    fullWidth: {
      true: 'w-full',
      false: 'w-fit',
    },
    size: {
      xs: 'gap-1',
      sm: 'gap-1',
      md: 'gap-2',
      lg: 'gap-2',
    },
    withBorder: {
      true: 'gap-0',
    },
    orientation: {
      horizontal: 'flex-row',
      vertical: 'flex-col',
    },
  },
});

const ToggleGroupBase: React.FC<ToggleGroupProps> = ({
  className,
  defaultValue,
  value,
  onValueChange,
  variant = 'default',
  size = 'md',
  shape = 'default',
  color = 'primary',
  orientation = 'horizontal',
  withBorder,
  isLoading,
  fullWidth,
  justify,
  icon,
  children,
  type = 'single',
  ...props
}) => {
  const { orderedSlots } = useOrderedSlots(children, {
    items: {
      Component: ToggleGroupItemSlot,
      props: {
        variant,
        size,
        shape,
        color,
        withBorder,
        orientation,
        isLoading,
        fullWidth,
        justify,
        icon,
        role: type == 'single' ? 'radio' : 'checkbox',
      },
    },
    tooltips: {
      Component: ToggleGroupTooltipSlot,
      props: {
        variant,
        size,
        shape,
        color,
        withBorder,
        orientation,
        isLoading,
        fullWidth,
        justify,
        icon,
        role: type == 'single' ? 'radio' : 'checkbox',
      },
    },
  });

  return type === 'multiple' ? (
    <ToggleGroupPrimitive.Root
      type="multiple"
      data-spellbook-name="toggle-group"
      value={value as string[] | undefined}
      onValueChange={onValueChange as (values: string[]) => void}
      defaultValue={defaultValue as string[] | undefined}
      className={cn(toggleGroupVariants({ fullWidth, withBorder, size, orientation }), className)}
      {...props}
    >
      {mapOrderedSlots(orderedSlots)}
    </ToggleGroupPrimitive.Root>
  ) : (
    <ToggleGroupPrimitive.Root
      type="single"
      data-spellbook-name="toggle-group"
      value={value as string | undefined}
      defaultValue={defaultValue as string | undefined}
      onValueChange={onValueChange as (value: string) => void}
      className={cn(toggleGroupVariants({ fullWidth, withBorder, size, orientation }), className)}
      {...props}
    >
      {mapOrderedSlots(orderedSlots)}
    </ToggleGroupPrimitive.Root>
  );
};

/**
 * Groups toggle buttons for single or multiple selection. Built on Radix UI ToggleGroup with compound-component slots for items, icons, and tooltips.
 *
 * ### When to use
 * - Switching between mutually exclusive views or modes (e.g., text alignment, list vs. grid view)
 * - Selecting one or more options from a small, visible set of choices
 * - Icon-only toolbar controls with optional tooltips for context
 *
 * ### When NOT to use
 * - For a single toggle action: use `ToggleButton` instead
 * - For navigation between pages or tabs: use `Tabs` instead
 * - For form fields with many options: use `RadioGroup` (single) or `CheckboxGroup` (multiple) instead
 * - For actions that trigger immediately (not selection state): use `Button` instead
 *
 * ### API
 * - `type`: `'single'` (default) or `'multiple'` — controls whether one or many items can be selected.
 * - `value` / `defaultValue`: Controlled or uncontrolled selected value(s). A `string` for single, `string[]` for multiple.
 * - `onValueChange`: Callback with the new value (`string` for single, `string[]` for multiple).
 * - `variant`, `size`, `shape`, `color`: Visual styling props inherited from `ToggleButton` variants.
 * - `icon`: When `true`, items render as `ToggleIconButton` components with icon-only layout.
 * - `withBorder`: Removes gaps and connects items with shared borders for a segmented-control look, joined side by side or top to bottom to match `orientation`.
 * - `fullWidth`: Stretches the group and its items to fill the container width.
 * - `orientation`: `'horizontal'` (default) or `'vertical'` layout direction.
 * - `isLoading`: Disables interaction and shows a spinner on all items.
 *
 * ### Composition (slots)
 * - `<ToggleGroup.Item>`: Individual toggle button item (required). Not the same as `ToggleButton` — it has similar behavior but is its own component.
 * - `<ToggleGroup.ItemLeadingIcon>`: Leading icon rendered before the item label.
 * - `<ToggleGroup.ItemTrailingIcon>`: Trailing icon rendered after the item label.
 * - `<ToggleGroup.ItemSpinner>`: Spinner shown when `isLoading` is true. Rendered by default.
 * - `<ToggleGroup.Tooltip>`: Wraps an item to show a tooltip on hover. Nest `Item` and `TooltipContent` inside.
 * - `<ToggleGroup.TooltipContent>`: Text content for the tooltip. Renders as a `Text` with `caption-1` variant.
 *
 * ### Visual states
 * - **Selected**: Item has pressed/active styling with the configured `color` theme
 * - **Unselected**: Default button appearance without active indicator
 * - **Disabled**: Muted appearance, interaction blocked
 * - **Loading**: Spinner replaces leading icon, interaction blocked
 * - **With border**: Items share connected borders with no gap, creating a segmented control appearance
 *
 * ### Accessibility
 * - Uses Radix UI `ToggleGroup.Root` providing `radiogroup` semantics for single and checkbox semantics for multiple
 * - Items receive `role="radio"` (single) or `role="checkbox"` (multiple) automatically
 * - Arrow keys navigate between items in single mode; Space/Enter toggles items
 * - When using `icon` mode, provide a `name` and optionally a `label` on each item so screen readers have accessible text
 */
export const ToggleGroup = Object.assign(ToggleGroupBase, {
  Item: ToggleGroupItemSlot,
  ItemLeadingIcon: ToggleGroupItemLeadingIconSlot,
  ItemTrailingIcon: ToggleGroupItemTrailingIconSlot,
  ItemSpinner: ToggleGroupItemSpinnerSlot,
  Tooltip: ToggleGroupTooltipSlot,
  TooltipContent: ToggleGroupTooltipContentSlot,
});
